import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ClienteRelatorioClientes,
  RelatorioClientes as Relatorio,
  VeiculoRelatorioClientes,
} from '@/features/relatorios/api/relatoriosApi';
import type { EstadoRelatorioClientes } from '@/features/relatorios/hooks/useRelatorioClientes';
import { ROTAS } from '@/app/rotas';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput } from '@/shared/ui/SearchInput';
import { IconAlert, IconSearch, IconUsers } from '@/shared/ui/Icon';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { formatarValorMonetario, useOcultarValores } from '@/shared/state/OcultarValoresContext';
import {
  ROTULOS_ORDENACAO,
  direcaoInicial,
  formatarData,
  formatarDocumento,
  formatarKm,
  formatarTelefoneCliente,
  fracaoPecas,
  type CampoOrdenacaoClientes,
} from './clientesRelatorio';
import './RelatorioClientes.css';

type Props = {
  estado: EstadoRelatorioClientes;
  /** Abre o histórico do veículo (aba "Veículo" da página). */
  onAbrirVeiculo?: (veiculoId: string) => void;
};

/**
 * Aba "Clientes" de Relatórios: quanto cada cliente gastou em serviços e
 * em peças no período, aberto por veículo. Tabela no desktop, cards no
 * celular — as duas versões vêm do mesmo estado.
 */
export function RelatorioClientes({ estado, onAbrirVeiculo }: Props) {
  const { dados, carregando, erro, recarregar } = estado;
  const { ocultar } = useOcultarValores();
  const moeda = (v: number) => formatarValorMonetario(v, ocultar);

  return (
    <div className="rcli">
      {erro && (
        <div className="alert alert--error" role="alert" style={{ flexWrap: 'wrap' }}>
          <IconAlert aria-hidden />
          <span style={{ flex: '1 1 240px' }}>Falha ao carregar o relatório de clientes: {erro}</span>
          <Button onClick={recarregar}>Tentar novamente</Button>
        </div>
      )}

      {!dados && carregando && <p className="hint">Carregando clientes…</p>}

      {dados && (
        <>
          <Indicadores resumo={dados.resumo} moeda={moeda} />
          <TabelaClientes
            estado={estado}
            dados={dados}
            moeda={moeda}
            ocultar={ocultar}
            onAbrirVeiculo={onAbrirVeiculo}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------

type Moeda = (v: number) => string;

function Kpi(props: { rotulo: string; valor: React.ReactNode; detalhe?: React.ReactNode }) {
  return (
    <div className="stat-card">
      <span className="stat-card__label">{props.rotulo}</span>
      <span className="stat-card__value">{props.valor}</span>
      {props.detalhe != null && <span className="rcli-kpi__detalhe">{props.detalhe}</span>}
    </div>
  );
}

function Indicadores({ resumo: r, moeda }: { resumo: Relatorio['resumo']; moeda: Moeda }) {
  const n = (v: number) => v.toLocaleString('pt-BR');
  return (
    <div className="rcli-kpis">
      <Kpi
        rotulo="Clientes atendidos"
        valor={n(r.clientesComCompra)}
        detalhe={r.clientes !== r.clientesComCompra ? `de ${n(r.clientes)} listados` : 'com O.S. no período'}
      />
      <Kpi rotulo="Veículos atendidos" valor={n(r.veiculos)} />
      <Kpi rotulo="Ordens de serviço" valor={n(r.ordens)} />
      <Kpi rotulo="Total em serviços" valor={moeda(r.totalServicos)} />
      <Kpi rotulo="Total em peças" valor={moeda(r.totalPecas)} />
      <Kpi
        rotulo="Ticket médio"
        valor={moeda(r.ticketMedio)}
        detalhe={`por O.S. • total ${moeda(r.total)}`}
      />
    </div>
  );
}

function BarraPecasServicos({ c, moeda }: { c: { totalPecas: number; totalServicos: number }; moeda: Moeda }) {
  const soma = c.totalPecas + c.totalServicos;
  if (soma <= 0) return null;
  const pct = fracaoPecas(c) * 100;
  return (
    <span
      className="rcli-barra"
      role="img"
      aria-label={`Serviços ${moeda(c.totalServicos)}, peças ${moeda(c.totalPecas)}`}
      title={`Serviços ${moeda(c.totalServicos)} • Peças ${moeda(c.totalPecas)}`}
    >
      <span className="rcli-barra__servicos" style={{ width: `${100 - pct}%` }} />
      <span className="rcli-barra__pecas" style={{ width: `${pct}%` }} />
    </span>
  );
}

function Filtros({ estado }: { estado: EstadoRelatorioClientes }) {
  return (
    <div className="rcli-filtros">
      <SearchInput
        valor={estado.busca}
        onChange={estado.setBusca}
        placeholder="Buscar por nome, telefone, documento ou placa…"
        ariaLabel="Buscar clientes"
      />
      <label className="rcli-ordenar">
        <span className="rcli-ordenar__rotulo">Ordenar por</span>
        <select
          className="input"
          value={`${estado.ordenacao.campo}:${estado.ordenacao.direcao}`}
          onChange={(e) => {
            const [campo, direcao] = e.target.value.split(':') as [CampoOrdenacaoClientes, 'asc' | 'desc'];
            estado.setOrdenacao({ campo, direcao });
          }}
        >
          {(Object.keys(ROTULOS_ORDENACAO) as CampoOrdenacaoClientes[]).flatMap((campo) => {
            const natural = direcaoInicial(campo);
            const outra: 'asc' | 'desc' = natural === 'asc' ? 'desc' : 'asc';
            const sufixo = (d: 'asc' | 'desc') =>
              campo === 'nome' ? (d === 'asc' ? 'A→Z' : 'Z→A') : campo === 'ultimaVisita'
                ? d === 'desc' ? 'mais recente' : 'mais antiga'
                : d === 'desc' ? 'maior' : 'menor';
            return [natural, outra].map((d) => (
              <option key={`${campo}:${d}`} value={`${campo}:${d}`}>
                {ROTULOS_ORDENACAO[campo]} ({sufixo(d)})
              </option>
            ));
          })}
        </select>
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={estado.somenteFechados}
          onChange={(e) => estado.setSomenteFechados(e.target.checked)}
        />
        Só O.S. fechadas
      </label>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={estado.incluirSemCompra}
          onChange={(e) => estado.setIncluirSemCompra(e.target.checked)}
        />
        Incluir clientes sem O.S. no período
      </label>
    </div>
  );
}

function CabecalhoOrdenavel({
  campo,
  estado,
  numerico,
  children,
}: {
  campo: CampoOrdenacaoClientes;
  estado: EstadoRelatorioClientes;
  numerico?: boolean;
  children: React.ReactNode;
}) {
  const ativo = estado.ordenacao.campo === campo;
  const sort = ativo ? (estado.ordenacao.direcao === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th className={numerico ? 'table__numeric' : undefined} aria-sort={sort}>
      <button
        type="button"
        className={`rcli-ordem${ativo ? ' rcli-ordem--ativa' : ''}`}
        onClick={() => estado.ordenarPor(campo)}
        title={`Ordenar por ${ROTULOS_ORDENACAO[campo].toLowerCase()}`}
      >
        {children}
        <span className="rcli-ordem__seta" aria-hidden>
          {ativo ? (estado.ordenacao.direcao === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function NomeCliente({ c }: { c: ClienteRelatorioClientes }) {
  const navigate = useNavigate();
  const tel = formatarTelefoneCliente(c.telefone);
  const doc = formatarDocumento(c.documento);
  const removido = c.nome === '(removido)';
  return (
    <span className="rcli-cliente">
      {removido ? (
        <span className="table__primary">{c.nome}</span>
      ) : (
        <button
          type="button"
          className="rcli-link"
          title="Abrir o cadastro do cliente"
          onClick={() => navigate(ROTAS.clienteEditar(c.clienteId))}
        >
          {c.nome}
        </button>
      )}
      {(tel || doc) && <span className="table__muted">{[tel, doc].filter(Boolean).join(' • ')}</span>}
    </span>
  );
}

function NomeVeiculo({
  v,
  onAbrirVeiculo,
}: {
  v: VeiculoRelatorioClientes;
  onAbrirVeiculo?: (id: string) => void;
}) {
  const extra = v.placa && v.descricao !== v.placa ? v.placa : null;
  if (!v.veiculoId || !onAbrirVeiculo) {
    return (
      <span className={v.veiculoId ? 'table__primary' : 'table__muted'}>
        {v.descricao}
        {extra ? ` • ${extra}` : ''}
      </span>
    );
  }
  const id = v.veiculoId;
  return (
    <button
      type="button"
      className="rcli-link"
      title="Ver o histórico deste veículo"
      onClick={() => onAbrirVeiculo(id)}
    >
      {v.descricao}
      {extra ? ` • ${extra}` : ''}
    </button>
  );
}

function TabelaClientes({
  estado,
  dados,
  moeda,
  ocultar,
  onAbrirVeiculo,
}: {
  estado: EstadoRelatorioClientes;
  dados: Relatorio;
  moeda: Moeda;
  /** Com valores ocultos a barra peças × serviços entregaria a proporção. */
  ocultar: boolean;
  onAbrirVeiculo?: (id: string) => void;
}) {
  const lista = estado.clientesOrdenados;
  const pag = usePaginacao(lista, 25);
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  const alternar = (id: string) =>
    setAbertos((s) => {
      const novo = new Set(s);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  const buscando = estado.buscaAplicada !== '';

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Clientes ({lista.length.toLocaleString('pt-BR')})</span>
        {estado.carregando && <span className="hint">Atualizando…</span>}
      </div>
      <Filtros estado={estado} />
      <div className={`card__body card__body--flush${estado.carregando ? ' rcli-carregando' : ''}`}>
        {lista.length === 0 ? (
          buscando ? (
            <div>
              <EmptyState
                icone={<IconSearch />}
                titulo="Nenhum cliente encontrado."
                descricao="Nenhum nome, telefone, documento ou placa corresponde à busca neste período."
              />
              <div className="rcli-vazio-acao">
                <Button onClick={() => estado.setBusca('')}>Limpar busca</Button>
              </div>
            </div>
          ) : (
            <EmptyState
              icone={<IconUsers />}
              titulo="Nenhum cliente com ordem de serviço no período."
              descricao={
                estado.incluirSemCompra
                  ? 'Não há clientes cadastrados para os filtros escolhidos.'
                  : 'Amplie o período ou marque "Incluir clientes sem O.S. no período" para ver todos os cadastros.'
              }
            />
          )
        ) : (
          <>
            {/* Desktop / notebook */}
            <div className="table-scroll rcli-tabela">
              <table className="table">
                <thead>
                  <tr>
                    <th className="rcli-col-expandir">
                      <span className="sr-only">Veículos</span>
                    </th>
                    <CabecalhoOrdenavel campo="nome" estado={estado}>
                      Cliente
                    </CabecalhoOrdenavel>
                    <th className="table__numeric">Veículos</th>
                    <CabecalhoOrdenavel campo="ordens" estado={estado} numerico>
                      O.S.
                    </CabecalhoOrdenavel>
                    <CabecalhoOrdenavel campo="ultimaVisita" estado={estado}>
                      Última visita
                    </CabecalhoOrdenavel>
                    <CabecalhoOrdenavel campo="servicos" estado={estado} numerico>
                      Serviços
                    </CabecalhoOrdenavel>
                    <CabecalhoOrdenavel campo="pecas" estado={estado} numerico>
                      Peças
                    </CabecalhoOrdenavel>
                    <CabecalhoOrdenavel campo="total" estado={estado} numerico>
                      Total
                    </CabecalhoOrdenavel>
                  </tr>
                </thead>
                <tbody>
                  {pag.paginados.map((c) => {
                    const aberto = abertos.has(c.clienteId);
                    const idDetalhe = `rcli-veic-${c.clienteId}`;
                    return (
                      <Fragment key={c.clienteId}>
                        <tr className={aberto ? 'rcli-linha--aberta' : undefined}>
                          <td className="rcli-col-expandir">
                            <button
                              type="button"
                              className="rcli-expandir"
                              aria-expanded={aberto}
                              aria-controls={idDetalhe}
                              aria-label={`${aberto ? 'Esconder' : 'Mostrar'} veículos de ${c.nome}`}
                              onClick={() => alternar(c.clienteId)}
                            >
                              <span aria-hidden>{aberto ? '▾' : '▸'}</span>
                            </button>
                          </td>
                          <td>
                            <NomeCliente c={c} />
                          </td>
                          <td className="table__numeric">{c.quantidadeVeiculos}</td>
                          <td className="table__numeric">{c.quantidadeOrdens}</td>
                          <td className="table__muted">{formatarData(c.ultimaVisita)}</td>
                          <td className="table__numeric">{moeda(c.totalServicos)}</td>
                          <td className="table__numeric">{moeda(c.totalPecas)}</td>
                          <td className="table__numeric">
                            <span className="rcli-total">
                              <span className="table__primary">{moeda(c.total)}</span>
                              {!ocultar && <BarraPecasServicos c={c} moeda={moeda} />}
                            </span>
                          </td>
                        </tr>
                        {aberto && (
                          <tr id={idDetalhe} className="rcli-detalhe">
                            <td />
                            <td colSpan={7}>
                              <VeiculosDoCliente c={c} moeda={moeda} onAbrirVeiculo={onAbrirVeiculo} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td className="table__primary">Totais</td>
                    <td />
                    <td className="table__numeric">{dados.resumo.ordens}</td>
                    <td />
                    <td className="table__numeric">{moeda(dados.resumo.totalServicos)}</td>
                    <td className="table__numeric">{moeda(dados.resumo.totalPecas)}</td>
                    <td className="table__numeric table__primary">{moeda(dados.resumo.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Tablet / celular */}
            <ul className="rcli-cards">
              {pag.paginados.map((c) => {
                const aberto = abertos.has(c.clienteId);
                const idDetalhe = `rcli-card-veic-${c.clienteId}`;
                return (
                  <li key={c.clienteId} className="rcli-card">
                    <div className="rcli-card__topo">
                      <NomeCliente c={c} />
                      <span className="rcli-card__total">{moeda(c.total)}</span>
                    </div>
                    {!ocultar && <BarraPecasServicos c={c} moeda={moeda} />}
                    <dl className="rcli-card__grade">
                      <div>
                        <dt>Serviços</dt>
                        <dd>{moeda(c.totalServicos)}</dd>
                      </div>
                      <div>
                        <dt>Peças</dt>
                        <dd>{moeda(c.totalPecas)}</dd>
                      </div>
                      <div>
                        <dt>O.S.</dt>
                        <dd>{c.quantidadeOrdens}</dd>
                      </div>
                      <div>
                        <dt>Última visita</dt>
                        <dd>{formatarData(c.ultimaVisita)}</dd>
                      </div>
                    </dl>
                    {c.veiculos.length > 0 && (
                      <button
                        type="button"
                        className="rcli-card__expandir"
                        aria-expanded={aberto}
                        aria-controls={idDetalhe}
                        onClick={() => alternar(c.clienteId)}
                      >
                        {aberto ? 'Esconder veículos' : `Ver veículos (${c.veiculos.length})`}
                        <span aria-hidden>{aberto ? '▴' : '▾'}</span>
                      </button>
                    )}
                    {aberto && (
                      <ul id={idDetalhe} className="rcli-card__veiculos">
                        {c.veiculos.map((v) => (
                          <li key={v.veiculoId ?? 'sem'} className="rcli-card__veiculo">
                            <div className="rcli-card__topo">
                              <NomeVeiculo v={v} onAbrirVeiculo={onAbrirVeiculo} />
                              <span className="table__primary">{moeda(v.total)}</span>
                            </div>
                            <span className="table__muted">
                              {v.quantidadeOrdens} O.S. • última {formatarData(v.ultimaVisita)}
                              {v.kmAtual != null ? ` • ${formatarKm(v.kmAtual)}` : ''}
                            </span>
                            <span className="table__muted">
                              Serviços {moeda(v.totalServicos)} • Peças {moeda(v.totalPecas)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>

            <Paginacao
              pagina={pag.pagina}
              totalPaginas={pag.totalPaginas}
              total={pag.total}
              tamanhoPagina={pag.tamanhoPagina}
              onMudarPagina={pag.setPagina}
            />
          </>
        )}
      </div>
    </div>
  );
}

function VeiculosDoCliente({
  c,
  moeda,
  onAbrirVeiculo,
}: {
  c: ClienteRelatorioClientes;
  moeda: Moeda;
  onAbrirVeiculo?: (id: string) => void;
}) {
  if (c.veiculos.length === 0) {
    return <p className="hint">Nenhum veículo vinculado a este cliente.</p>;
  }
  return (
    <table className="table rcli-subtabela">
      <thead>
        <tr>
          <th>Veículo</th>
          <th>KM atual</th>
          <th className="table__numeric">O.S.</th>
          <th>Última visita</th>
          <th className="table__numeric">Serviços</th>
          <th className="table__numeric">Peças</th>
          <th className="table__numeric">Total</th>
        </tr>
      </thead>
      <tbody>
        {c.veiculos.map((v) => (
          <tr key={v.veiculoId ?? 'sem'}>
            <td>
              <NomeVeiculo v={v} onAbrirVeiculo={onAbrirVeiculo} />
            </td>
            <td className="table__muted">{formatarKm(v.kmAtual)}</td>
            <td className="table__numeric">{v.quantidadeOrdens}</td>
            <td className="table__muted">{formatarData(v.ultimaVisita)}</td>
            <td className="table__numeric">{moeda(v.totalServicos)}</td>
            <td className="table__numeric">{moeda(v.totalPecas)}</td>
            <td className="table__numeric table__primary">{moeda(v.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
